/**
 * Client-side store for **host context** — the per-turn, non-authoritative
 * hint describing what the user is currently viewing in an embedded host
 * (e.g. company/project/RFI in the Procore side panel).
 *
 * Keyed by `AppSurface` and backed by `sessionStorage` so a host shell can
 * capture context (Procore captures it via the `postMessage` `setup`
 * handshake) and the chat composer can read it at send-time — surviving the
 * bootstrap → `/chat` hard redirect. The stored `data` is opaque here; only
 * the per-surface server renderer (`@workspace/ai` `hostContextRenderers`)
 * interprets it.
 */

import type { AppSurface } from "./host";

const KEY_PREFIX = "magnus-host-context:";

const keyFor = (surface: AppSurface) => `${KEY_PREFIX}${surface}`;

/** Persist the captured host context for a surface. No-op if storage is unavailable. */
export function setHostContext(surface: AppSurface, data: unknown): void {
  try {
    sessionStorage.setItem(keyFor(surface), JSON.stringify(data));
  } catch {
    // sessionStorage unavailable (private mode / partitioned iframe): the
    // model just won't get the host-context hint this session.
  }
}

/** Read the captured host context for a surface (`null` when absent). */
export function getHostContext(surface: AppSurface): unknown {
  try {
    const raw = sessionStorage.getItem(keyFor(surface));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Clear a surface's host context (e.g. on sign-out). */
export function clearHostContext(surface: AppSurface): void {
  try {
    sessionStorage.removeItem(keyFor(surface));
  } catch {
    // ignore
  }
}
